import { escapeAttr, escapeText } from '../email-editor/draftConvertCore';

type AgentEmailVariableSuggestion = {
  id: string;
  name: string;
  value?: string | null;
};

// Runs on sanitizeAgentEmailHtml's output, which is well-formed and escaped,
// so a tag never contains `>` and text never contains `<`.
const TOKEN_RE = /<[^>]*>|[^<]+/g;
// Not after `&` (`&#x41;` is a character reference), a word character or `/`
// (`p#top`, `https://x.ch/#top`): those are not variables.
const VARIABLE_RE =
  /(?<![&\w/])#[A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)*/g;
const LINK_OPEN_RE = /^<a[\s>]/i;
const LINK_CLOSE_RE = /^<\/a[\s>]/i;

const replaceVariables = (
  text: string,
  replace: (id: string) => string | null,
): string => text.replace(VARIABLE_RE, (id: string) => replace(id) ?? id);

const variableDisplay = ({
  name,
  value,
}: AgentEmailVariableSuggestion): string =>
  value != null && value !== '' ? value : name;

export const hasAgentEmailVariables = (html: string): boolean =>
  Array.from(html.matchAll(TOKEN_RE), ([token]) => token).some(
    token =>
      !token.startsWith('<') && replaceVariables(token, () => '') !== token,
  );

/**
 * Turns the agent's `#variable` tokens into the editor's variable chips. Only
 * ids present in `suggestions` with a value are linked: an empty one would be
 * sent blank, so it stays visible as text. Tags and link text are left as is.
 */
export const linkAgentEmailVariables = (
  html: string,
  suggestions: ReadonlyArray<AgentEmailVariableSuggestion>,
): string => {
  const known = new Map(
    suggestions
      .filter(suggestion => variableDisplay(suggestion).trim() !== '')
      .map(suggestion => [suggestion.id, suggestion]),
  );
  if (known.size === 0) {
    return html;
  }
  let inLink = false;
  return html.replace(TOKEN_RE, token => {
    if (token.startsWith('<')) {
      if (LINK_OPEN_RE.test(token)) {
        inLink = true;
      } else if (LINK_CLOSE_RE.test(token)) {
        inLink = false;
      }
      return token;
    }
    if (inLink) {
      return token;
    }
    return replaceVariables(token, id => {
      const suggestion = known.get(id);
      return suggestion == null
        ? null
        : `<span data-ra-var="${escapeAttr(id)}">${escapeText(
            variableDisplay(suggestion),
          )}</span>`;
    });
  });
};
